import {
  CONTENT_LANGS,
  type ContentLang,
  contentLangOf,
} from "@fazly/shared/contentLang";

import { APP_STORE_URL, APPLE_ITUNES_ID, PLAY_STORE_URL } from "./stores";

/** The canonical public origin of the web app. Change to your domain. */
export const SITE_URL = "https://fazly.app";

/** Absolute canonical URL for a path (e.g. canonical("/privacy")). */
export function canonical(path: string): string {
  return new URL(path, SITE_URL).href;
}

/**
 * The response headers every server-rendered page sends.
 *
 * ── Cache-Control ────────────────────────────────────────────────────────────
 *
 * The HTML carried no cache header at all, so every request re-rendered the page
 * in a Worker isolate — a measured 440–580 ms of TTFB for a page whose content
 * depends on nothing but the URL. Nothing on this site is per-visitor: no
 * sign-in, no session, no personalised block, not even on the share pages (see
 * `ShareLanding` — it deliberately renders no shared content).
 *
 * So the edge is allowed to keep it. `max-age=0` keeps the BROWSER honest — a
 * visitor who reloads after a deploy gets the new page — while `s-maxage` lets
 * Cloudflare answer from cache, and `stale-while-revalidate` means the one
 * request that finds it expired is served instantly from the stale copy while
 * the refresh happens behind it. Ten minutes rather than an hour because a
 * deploy does not purge the cache, and ten minutes is the longest anyone should
 * have to wait to see a fix they just shipped.
 *
 * ── HSTS ─────────────────────────────────────────────────────────────────────
 *
 * `http://` already 301s to `https://`, but a redirect is one plaintext round
 * trip an attacker on the network can intercept. HSTS removes it: once the
 * browser sees this header, it rewrites `http://fazly.app` internally and
 * never sends the first request.
 *
 * A year, and NO `includeSubDomains`. The header is host-scoped, so this covers
 * the apex; `includeSubDomains` would also bind `api.` and `dev-api.`, which is
 * a promise about hosts this route knows nothing about and cannot un-make for
 * the life of the max-age. No `preload` for the same reason — that one is
 * irreversible on a timescale measured in browser releases.
 */
/**
 * The one Content-Security-Policy directive this site needs, and the measured
 * reason it needs it.
 *
 * ── What was leaving, and why it mattered ────────────────────────────────────
 *
 * MediaPipe Tasks Vision POSTs to `https://odml.pa.googleapis.com/v1/log` when
 * the FaceLandmarker initialises. Measured on a real deployment, not inferred:
 * one `fetch`, 436 ms, fired on a tool page where the WASM and the model had
 * both come from this origin.
 *
 * The photograph is not in it — there is still no code path that transmits the
 * image, and that claim survives. What did not survive is the second half of
 * what this site says about itself. `lib/llms.ts` published the sentence "the
 * WASM and the model are served from fazly.app itself rather than a CDN, so no
 * third party learns who opened a scanning page", and a telemetry POST to
 * Google on page load makes that false. Every page here is written to be
 * checkable in the network tab; a reader who checked would have found us out.
 *
 * ── Why a CSP header rather than a note in the privacy page ──────────────────
 *
 * Because a note is a promise and this is enforcement. `connect-src` is applied
 * by the browser before the request leaves, so the claim stops depending on
 * MediaPipe's future behaviour — a version bump that adds a second endpoint is
 * blocked by the same line rather than quietly reopening the hole.
 *
 * Only `connect-src` is set, deliberately. A full policy is a large surface
 * with a real chance of breaking a page in a way nobody notices until a user
 * reports a blank screen, and none of the other directives are load-bearing
 * here: this site loads no third-party scripts, embeds no frames and takes no
 * user input. The narrow directive fixes the specific untruth and risks
 * nothing else.
 *
 * `'self'` covers the page's own fetches and the model files. The two PostHog
 * hosts are the analytics endpoint and its static assets — see `analytics.ts`,
 * where the whole integration is already inert without a key. Google Fonts are
 * absent because the fonts are self-hosted (`styles.css`).
 */
const CONNECT_SRC = [
  "'self'",
  "https://us.i.posthog.com",
  "https://us-assets.i.posthog.com",
].join(" ");

export function documentHeaders() {
  return {
    "cache-control":
      "public, max-age=0, s-maxage=600, stale-while-revalidate=86400",
    "strict-transport-security": "max-age=31536000",
    "content-security-policy": `connect-src ${CONNECT_SRC}`,
  };
}

/**
 * The language to put on `<html lang>` for a URL.
 *
 * `<html lang>` is not decoration: a screen reader picks its voice from it, and
 * a page that claims `en` while showing Korean is read out by an English
 * synthesiser one syllable at a time. Search engines use it as a language
 * signal alongside hreflang, and disagreeing with hreflang is worse than saying
 * nothing.
 *
 * Two URL shapes carry a language and they do it differently, which is why this
 * cannot just read one of them: the landing page puts it in the first path
 * segment (`/ko`), the legal pages put it in a query string (`/privacy?lang=ko`).
 * Anything else — `/support`, `/delete`, a share link — is English.
 */
export function documentLang(pathname: string, search: string): ContentLang {
  const fromPath = contentLangOf(pathname.split("/")[1]);
  if (fromPath) return fromPath;
  const fromQuery = contentLangOf(
    new URLSearchParams(search).get("lang") ?? undefined,
  );
  return fromQuery ?? "en";
}

/**
 * The locale tag Facebook's Open Graph wants, which is NOT a language code.
 *
 * `og:locale` is `language_TERRITORY` and a bare `es` is ignored, so the choice
 * of territory is forced on us. Each one below is the territory the store
 * listing is actually published for, which is where the traffic arrives from —
 * the reasoning that stood here picked territories by Christian population,
 * which is the reference implementation's and has nothing to do with this
 * product.
 */
export const OG_LOCALE: Record<ContentLang, string> = {
  en: "en_US",
  de: "de_DE",
  tr: "tr_TR",
  // pt_BR, not pt_PT: the store listing is published as pt-BR and that is where
  // the traffic is.
  pt: "pt_BR",
  es: "es_ES",
};

/**
 * `<link rel="alternate" hreflang>` for every language of one page, plus
 * `x-default`.
 *
 * Two rules Google enforces and neither is obvious from the docs:
 *
 *  1. **The set must be complete and self-referential.** Every URL in the group
 *     has to list every other URL *and itself*, or Google throws the whole
 *     cluster away — a page that names every alternate but not itself is not
 *     "almost right", it is ignored.
 *  2. **`x-default` is not "English".** It is the page a visitor lands on when
 *     no language matches, which here is the root — the same URL as `en`, listed
 *     twice on purpose.
 *
 * `pathFor` maps a language to that page's path in it, so the same helper serves
 * the landing page (`/`, `/es`) and the legal pages (`/privacy?lang=es`).
 */
export function alternates(pathFor: (lang: ContentLang) => string): {
  rel: string;
  hrefLang: string;
  href: string;
}[] {
  const links = CONTENT_LANGS.map((lang) => ({
    rel: "alternate",
    hrefLang: lang,
    href: canonical(pathFor(lang)),
  }));
  return [
    ...links,
    { rel: "alternate", hrefLang: "x-default", href: canonical(pathFor("en")) },
  ];
}

/**
 * Open Graph + Twitter card meta for one page.
 *
 * Both vocabularies are emitted because they are read by different things:
 * Facebook, LinkedIn, WhatsApp, iMessage and Slack read `og:*`, while X reads
 * `twitter:*` and falls back to `og:*` only for the fields it has no name of its
 * own for. `summary_large_image` rather than `summary`, since the card carries a
 * 1200×630 image and the small card would letterbox it into a thumbnail.
 */
export function socialMeta({
  title,
  description,
  path,
  lang,
  image = ogImage(lang),
  imageAlt,
}: {
  title: string;
  description: string;
  path: string;
  lang: ContentLang;
  image?: string;
  imageAlt?: string;
}): { property?: string; name?: string; content: string }[] {
  const url = canonical(path);
  const absoluteImage = canonical(image);
  return [
    { property: "og:type", content: "website" },
    { property: "og:site_name", content: "Fazly" },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:image", content: absoluteImage },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    ...(imageAlt ? [{ property: "og:image:alt", content: imageAlt }] : []),
    { property: "og:locale", content: OG_LOCALE[lang] },
    // NO `og:locale:alternate`, and not because the other four would be wrong.
    // TanStack's head renderer de-duplicates meta by `name`/`property`
    // (`headContentUtils.buildTagsFromMatches`), so a repeated property survives
    // exactly once — the shipped page carried a single alternate on every one of
    // the languages, which is worse than silence.
    //
    // Nothing is lost. Open Graph alternates describe locale variants served at
    // ONE url; here each language has its own url, which is what the hreflang
    // set above states properly.
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: absoluteImage },
    ...(imageAlt ? [{ name: "twitter:image:alt", content: imageAlt }] : []),
  ];
}

/**
 * The share card for a language — what a link to this site looks like when it
 * is pasted into WhatsApp, iMessage, Facebook or Slack.
 *
 * One per language, drawn by `scripts/og-image.py` and committed. See that file
 * for why they are pre-rendered and how they are kept from drifting.
 */
export function ogImage(lang: ContentLang): string {
  return `/og/${lang}.png`;
}

/**
 * The app itself, as schema.org `MobileApplication`.
 *
 * This is the one piece of structured data with a chance of changing what a
 * result LOOKS like — an app with a price, a category and a rating can render
 * with them attached. Deliberately NO `aggregateRating`: Google requires ratings
 * to be visible on the page they are marked up on, and inventing one is the
 * fastest way to a manual action.
 *
 * `offers` states the free download rather than the subscription price, because
 * that is what the store page says and the two have to agree. StoreKit charges
 * in the buyer's currency across 175 storefronts, so a number here would be
 * wrong in 174 of them (same reasoning as `lib/legal.ts`'s plan list).
 */
export function appJsonLd(name: string, description: string) {
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    name,
    description,
    applicationCategory: "LifestyleApplication",
    operatingSystem: "iOS, Android",
    url: SITE_URL,
    installUrl: [APP_STORE_URL, PLAY_STORE_URL],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      category: "free",
    },
    author: { "@type": "Organization", name: "AKES Solutions Limited" },
    identifier: APPLE_ITUNES_ID,
  };
}

/**
 * The ten browser tools, as one free `WebApplication`, for the landing page.
 *
 * The landing page used to describe exactly one thing in structured data: the
 * `MobileApplication`, whose offer is a free DOWNLOAD. That is true and it is
 * the less important half of what the page offers. Most visitors who arrive on
 * `/` from an assistant are asking for a free test, and the free test is the
 * ten tools — which the tool pages each mark up as a `SoftwareApplication` with
 * `isAccessibleForFree`, and which the landing page, the most-visited URL on
 * the site, said nothing about at all. So an extractor reading the homepage
 * learned "free app" and not "free test in your browser, no install".
 *
 * `operatingSystem` names the browser rather than the phone for the same
 * reason `tools/head.ts` does: the distinction IS the pitch.
 */
export function freeToolsJsonLd({
  name,
  description,
  path,
  lang,
}: {
  name: string;
  description: string;
  path: string;
  lang: ContentLang;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name,
    description,
    url: canonical(path),
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Any (runs in a web browser)",
    browserRequirements: "Requires JavaScript and WebAssembly",
    inLanguage: lang,
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      category: "free",
    },
    publisher: { "@type": "Organization", name: "AKES Solutions Limited" },
  };
}

/**
 * The FAQ, as schema.org `FAQPage`.
 *
 * Rich results for FAQs are restricted to authoritative sites, so this is not
 * free stars — it is here because the same markup is what AI search reads when
 * it answers "does fazly work on android", and an answer it can quote verbatim
 * is the difference between being cited and being summarised away.
 * Every question below is also visible on the page, which Google requires.
 */
export function faqJsonLd(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

/**
 * One article, as schema.org `BlogPosting`.
 *
 * The fields that do work here are the dates and the publisher. Google uses
 * `datePublished` and `dateModified` to decide how fresh a result is, and for
 * how-to and advice queries — which is all of this blog — freshness is a real
 * ranking input rather than a vanity signal.
 *
 * `author` is the ORGANISATION rather than a person, deliberately. An invented
 * byline is the cheapest possible E-E-A-T signal and the easiest to see through;
 * naming the company that makes the app is both true and checkable.
 */
export function articleJsonLd({
  headline,
  description,
  path,
  published,
  updated,
}: {
  headline: string;
  description: string;
  path: string;
  published: string;
  updated?: string;
}) {
  const url = canonical(path);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline,
    description,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    datePublished: published,
    dateModified: updated ?? published,
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      name: "AKES Solutions Limited",
      logo: { "@type": "ImageObject", url: canonical("/icon-512.png") },
    },
    author: { "@type": "Organization", name: "Fazly" },
  };
}

/**
 * The trail from the home page to here, as `BreadcrumbList`.
 *
 * This is the one piece of structured data on the blog with a visible payoff:
 * Google replaces the URL line in a result with the breadcrumb, so a result for
 * an article reads "Fazly › Blog › …" instead of a naked slug.
 */
export function breadcrumbJsonLd(trail: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((step, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: step.name,
      item: canonical(step.path),
    })),
  };
}

/**
 * The site itself, as schema.org `WebSite`.
 *
 * No `potentialAction`/`SearchAction`: that markup claims the site has a search
 * results URL a crawler can drive, and this one does not. Declaring it anyway is
 * the most common piece of copied-in structured data on the web and it is a
 * claim about a page that would 404.
 *
 * What it does earn is the entity link — `WebSite` naming the same publisher as
 * the app and the articles is what lets a search engine treat fazly.app,
 * the App Store listing and the blog as one thing rather than three.
 */
export function websiteJsonLd(name: string, description: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name,
    description,
    url: SITE_URL,
    inLanguage: CONTENT_LANGS,
    publisher: { "@type": "Organization", name: "AKES Solutions Limited" },
  };
}

/**
 * A list of pages, as `CollectionPage` + `ItemList`.
 *
 * The index page is otherwise indistinguishable from any other page with links
 * on it. This says what the links ARE and what order they are in, which is how
 * a crawler learns that `/tools` is a hub rather than a leaf — and hubs get
 * crawled more often, which is most of why new pages get discovered quickly.
 */
export function collectionJsonLd({
  name,
  description,
  path,
  items,
}: {
  name: string;
  description: string;
  path: string;
  items: { name: string; path: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url: canonical(path),
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        url: canonical(item.path),
      })),
    },
  };
}

/** The publisher, so the app and the legal pages resolve to one entity. */
export function organizationJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "AKES Solutions Limited",
    url: SITE_URL,
    logo: canonical("/icon-512.png"),
    // The other places this same entity exists. `sameAs` is how a search engine
    // is told that the App Store listing, the Play listing and this domain are
    // one thing rather than three unrelated results — which is the difference
    // between a brand that resolves to an entity and a brand that is a string.
    sameAs: [APP_STORE_URL, PLAY_STORE_URL],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@fazly.app",
      url: canonical("/support"),
    },
  };
}
